import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { toast } from "react-toastify";
import Select from "react-select";
import { types as OPPORTUNITY_TYPES } from "../lib/opportunityTypes";

const PROGRAM_TYPES = [
  "Biology",
  "Business Administration",
  "Chemical Engineering",
  "Chemistry",
  "Computer Science",
  "Electrical Engineering",
  "Mechanical Engineering",
  "Physics",
  "Psychology"
];

const AddOpportunityModal = ({ isOpen, onClose, onSubmit }) => {
  // State variables for form fields
  const [programType, setProgramType] = useState("");
  const [hostName, setHostName] = useState("");
  const [hostPhoneNumber, setHostPhoneNumber] = useState("");
  const [contactPerson, setContactPerson] = useState("");
  const [hostSite, setHostSite] = useState("");
  const [streetAddress, setStreetAddress] = useState("");
  const [city, setCity] = useState("");
  const [stateProvince, setStateProvince] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [description, setDescription] = useState("");
  const [requiredDress, setRequiredDress] = useState("");
  const [opportunityType, setOpportunityType] = useState([]);
  const [isActive, setIsActive] = useState(true); // New state variable for Active/Inactive

  // Schedule state
  const [schedule, setSchedule] = useState({});

  // Site Visit fields
  const [siteVisit, setSiteVisit] = useState("No"); // Possible values: 'Yes', 'No', 'N/A'
  const [dateOfVisit, setDateOfVisit] = useState("");
  const [visitBy, setVisitBy] = useState("");

  // Days of the week
  const daysOfWeek = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  // Tab state
  const [activeTab, setActiveTab] = useState("host"); // 'host' or 'details' or 'schedule'

  // Add this useEffect to reset the tab when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab("host");
    }
  }, [isOpen]);

  // Handle schedule changes
  const handleDayToggle = (day) => {
    setSchedule((prevSchedule) => {
      if (prevSchedule[day]) {
        // Remove the day if it's already in the schedule
        const { [day]: _, ...rest } = prevSchedule;
        return rest;
      } else {
        // Add the day with default start and end times
        return {
          ...prevSchedule,
          [day]: { startTime: "", endTime: "" },
        };
      }
    });
  };

  const handleTimeChange = (day, field, value) => {
    setSchedule((prevSchedule) => ({
      ...prevSchedule,
      [day]: {
        ...prevSchedule[day],
        [field]: value,
      },
    }));
  };

  const validateStep = (step) => {
    switch (step) {
      case "host":
        if (!hostName || !hostPhoneNumber) {
          toast.error("Please fill in all required Host Information fields.");
          setActiveTab("host");
          return false;
        }
        if (!streetAddress || !city || !stateProvince || !postalCode) {
          toast.error("Please fill in the complete address information.");
          setActiveTab("host");
          return false;
        }
        return true;
      case "details":
        if (!opportunityType) {
          toast.error("Please select an Opportunity Type.");
          return false;
        }
        return true;
      case "schedule":
        if (siteVisit === "Yes") {
          if (!dateOfVisit) {
            toast.error("Please select a date of visit.");
            setActiveTab("schedule");
            return false;
          }
          if (!visitBy) {
            toast.error("Please enter the name of the visitor.");
            setActiveTab("schedule");
            return false;
          }
        }
        return true;
      case "final":
        if (Object.keys(schedule).length === 0) {
          toast.info("Please select at least one day in the schedule.");
          setActiveTab("schedule");
          return false;
        }
        for (const day in schedule) {
          const { startTime, endTime } = schedule[day];
          if (!startTime || !endTime) {
            toast.error(`Please enter start and end times for ${day}.`);
            setActiveTab("schedule");
            return false;
          }
          if (startTime >= endTime) {
            toast.error(`End time must be after start time for ${day}.`);
            setActiveTab("schedule");
            return false;
          }
        }
        return true;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (activeTab === "host" && validateStep("host")) {
      setActiveTab("details");
    } else if (activeTab === "details" && validateStep("details")) {
      setActiveTab("schedule");
    }
  };

  const handleBack = () => {
    if (activeTab === "schedule") {
      setActiveTab("details");
    } else if (activeTab === "details") {
      setActiveTab("host");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate all steps before submission
    if (!validateStep("host")) {
      setActiveTab("host");
      return;
    }
    if (!validateStep("details")) {
      setActiveTab("details");
      return;
    }
    if (!validateStep("schedule")) {
      setActiveTab("schedule");
      return;
    }
    // Add final validation for schedule
    if (!validateStep("final")) {
      return;
    }

    // If all validations pass, submit the form
    const newOpportunity = {
      id: Date.now() + Math.random(),
      programType,
      hostName,
      hostPhoneNumber,
      hostSite,
      streetAddress,
      city,
      stateProvince,
      postalCode,
      description,
      requiredDress,
      opportunityType,
      isActive,
      schedule,
      siteVisit: siteVisit === "Yes",
      dateOfVisit: siteVisit === "Yes" ? dateOfVisit : null,
      visitBy: siteVisit === "Yes" ? visitBy : null,
      students: [],
    };

    onSubmit(newOpportunity);
    toast.success("Opportunity added successfully!");

    // Reset form fields
    setHostName("");
    setHostPhoneNumber("");
    setHostSite("");
    setStreetAddress("");
    setCity("");
    setStateProvince("");
    setPostalCode("");
    setDescription("");
    setRequiredDress("");
    setOpportunityType("");
    setIsActive(true);
    setSchedule({});
    setSiteVisit("No");
    setDateOfVisit("");
    setVisitBy("");
    setActiveTab("host");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div className="bg-white rounded-lg w-full max-w-3xl mx-4 p-4 md:p-6 relative overflow-y-auto max-h-[90vh]">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
          aria-label="Close Modal"
        >
          <X size={20} />
        </button>
        <h2 id="modal-title" className="text-xl font-bold mb-6">
          Add new opportunity
        </h2>
        {/* Tab Navigation */}
        <div className="mb-8">
          <nav className="flex space-x-4 border-b">
            <button
              type="button"
              onClick={() => setActiveTab("host")}
              className={`py-2 px-4 ${
                activeTab === "host"
                  ? "border-b-2 border-blue-600 text-blue-600"
                  : "text-gray-600 hover:text-blue-600"
              }`}
            >
              Host Information
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("details")}
              className={`py-2 px-4 ${
                activeTab === "details"
                  ? "border-b-2 border-blue-600 text-blue-600"
                  : "text-gray-600 hover:text-blue-600"
              }`}
            >
              Opportunity Details
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("schedule")}
              className={`py-2 px-4 ${
                activeTab === "schedule"
                  ? "border-b-2 border-blue-600 text-blue-600"
                  : "text-gray-600 hover:text-blue-600"
              }`}
            >
              Schedule
            </button>
          </nav>
        </div>
        <form onSubmit={handleSubmit}>
          {/* Host Information Tab */}
          {activeTab === "host" && (
            <div className="space-y-6">
              {/* Host Details */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Host Site */}
                {/* <div>
                  <label className="block text-gray-700">
                    Host Site<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className="w-full border rounded px-3 py-2"
                    value={hostSite}
                    onChange={(e) => setHostSite(e.target.value)}
                    required
                    placeholder="Enter host site"
                  />
                </div> */}
                {/* Host Name */}
                <div>
                  <label className="block text-gray-700">
                    Host Name<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className="w-full border rounded px-3 py-2"
                    value={hostName}
                    onChange={(e) => setHostName(e.target.value)}
                    required
                    placeholder="Enter host name"
                  />
                </div>
                {/* Host Phone Number */}
                <div>
                  <label className="block text-gray-700">
                    Host Phone Number<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    className="w-full border rounded px-3 py-2"
                    value={hostPhoneNumber}
                    onChange={(e) => setHostPhoneNumber(e.target.value)}
                    required
                    placeholder="Enter host phone number"
                  />
                </div>
                <div>
                  <label className="block text-gray-700">
                    Contact Person
                  </label>
                  <input
                    type="text"
                    className="w-full border rounded px-3 py-2"
                    value={contactPerson}
                    onChange={(e) => setContactPerson(e.target.value)}
                    placeholder="Enter contact person name"
                  />
                </div>
              </div>
              {/* Address Information */}
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-700">
                    Street Address<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className="w-full border rounded px-3 py-2"
                    value={streetAddress}
                    required
                    onChange={(e) => setStreetAddress(e.target.value)}
                    placeholder="Enter street address"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-gray-700">
                      City<span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className="w-full border rounded px-3 py-2"
                      value={city}
                      required
                      onChange={(e) => setCity(e.target.value)}
                      placeholder="Enter city"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700">
                      State/Province<span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className="w-full border rounded px-3 py-2"
                      value={stateProvince}
                      required
                      onChange={(e) => setStateProvince(e.target.value)}
                      placeholder="Enter state or province"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700">
                      Postal Code<span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className="w-full border rounded px-3 py-2"
                      value={postalCode}
                      required
                      onChange={(e) => setPostalCode(e.target.value)}
                      placeholder="Enter postal code"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Opportunity Details Tab */}
          {activeTab === "details" && (
            <div className="space-y-6">
              <div className="space-y-4">
                {/* Active/Inactive Checkbox */}
                <div>
                  <label className="block text-gray-700">Status</label>
                  <div className="flex items-center mt-2">
                    <input
                      type="checkbox"
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                      className="mr-2"
                    />
                    <span>{isActive ? "Active" : "Inactive"}</span>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Program Type Field */}
                  <div>
                    <label className="block text-gray-700">
                      Program<span className="text-red-500">*</span>
                    </label>
                    <Select
                      value={programType ? { value: programType, label: programType } : null}
                      onChange={(selected) => setProgramType(selected ? selected.value : "")}
                      options={PROGRAM_TYPES.map(type => ({ value: type, label: type }))}
                      className="mt-1"
                      placeholder="Select program type"
                      isSearchable
                      required
                    />
                  </div>
                  {/* Opportunity Type Field */}
                  <div>
                    <label className="block text-gray-700">
                      Opportunity Type<span className="text-red-500">*</span>
                    </label>
                    <Select
                      isMulti
                      value={OPPORTUNITY_TYPES.filter(
                        (type) => opportunityType.includes(type.value)
                      )}
                      onChange={(selected) =>
                        setOpportunityType(selected.map(option => option.value))
                      }
                      options={OPPORTUNITY_TYPES}
                      className="mt-1"
                      placeholder="Select opportunity types"
                      isSearchable
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-gray-700">Description</label>
                  <textarea
                    className="w-full border rounded px-3 py-2"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Enter description"
                    rows="4"
                  />
                </div>
                <div>
                  <label className="block text-gray-700">Required Dress</label>
                  <input
                    type="text"
                    className="w-full border rounded px-3 py-2"
                    value={requiredDress}
                    onChange={(e) => setRequiredDress(e.target.value)}
                    placeholder="Enter required dress"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Schedule Information Tab */}
          {activeTab === "schedule" && (
            <div className="space-y-6">
              <div>
                <label className="block text-gray-700">Site Visit</label>
                <div className="flex items-center space-x-4 mt-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="siteVisit"
                      value="Yes"
                      checked={siteVisit === "Yes"}
                      onChange={(e) => setSiteVisit(e.target.value)}
                      className="mr-2"
                    />
                    Yes
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="siteVisit"
                      value="No"
                      checked={siteVisit === "No"}
                      onChange={(e) => setSiteVisit(e.target.value)}
                      className="mr-2"
                    />
                    No
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="siteVisit"
                      value="N/A"
                      checked={siteVisit === "N/A"}
                      onChange={(e) => setSiteVisit(e.target.value)}
                      className="mr-2"
                    />
                    N/A
                  </label>
                </div>
                {siteVisit === "Yes" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-gray-700">
                        Date of Visit<span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        className="w-full border rounded px-3 py-2"
                        value={dateOfVisit}
                        onChange={(e) => setDateOfVisit(e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700">
                        Visit By<span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        className="w-full border rounded px-3 py-2"
                        value={visitBy}
                        onChange={(e) => setVisitBy(e.target.value)}
                        required
                        placeholder="Enter visitor's name"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-gray-700">
                  Potential Schedule
                </label>
                <div className="mt-2 space-y-4">
                  {daysOfWeek.map((day) => (
                    <div key={day} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={schedule.hasOwnProperty(day)}
                        onChange={() => handleDayToggle(day)}
                        className="mr-2"
                      />
                      <label className="mr-4">{day}</label>
                      {schedule.hasOwnProperty(day) && (
                        <div className="flex space-x-2">
                          <input
                            type="time"
                            value={schedule[day].startTime}
                            onChange={(e) =>
                              handleTimeChange(day, "startTime", e.target.value)
                            }
                            className="border rounded px-2 py-1"
                            required
                          />
                          <span>to</span>
                          <input
                            type="time"
                            value={schedule[day].endTime}
                            onChange={(e) =>
                              handleTimeChange(day, "endTime", e.target.value)
                            }
                            className="border rounded px-2 py-1"
                            required
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Form Buttons */}
          <div className="flex justify-between mt-6">
            <button
              type="button"
              onClick={handleBack}
              className={`bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-4 rounded ${
                activeTab === "host" ? "invisible" : ""
              }`}
            >
              Back
            </button>
            <div className="ml-auto">
              {activeTab !== "schedule" ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded"
                >
                  Next
                </button>
              ) : (
                <button
                  type="submit"
                  className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded"
                >
                  Submit
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddOpportunityModal;
